import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TextField,
  Rating,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { RestartAlt } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { API_ORIGIN as API_URL } from "../../config/env";
import { getAuthHeaders } from "../../utils/auth";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import {
  AdminPageLayout,
  AdminSectionCard,
  TableRowActions,
  DeleteConfirmationDialog,
  AdminAction,
} from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";
import ViewFeedbackDialog from "./ViewFeedbackDialog";

function getUserName(feedback) {
  return feedback?.user?.name || "";
}

function getMessagePreview(message) {
  const text = String(message || "").trim();
  if (!text) return null;
  return text;
}

export default function FeedbackListPage() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [filterKey, setFilterKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [viewTarget, setViewTarget] = useState(null);

  const hasActiveFilters = !!searchInput.trim();

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/feedback`, {
        headers: getAuthHeaders(),
        params: { page: 1, limit: 10000 },
      });
      setFeedbacks(res.data?.data?.feedbacks || []);
    } catch (err) {
      toast.error("Failed to fetch feedbacks");
      setFeedbacks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [searchInput]);

  const filteredRows = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    if (!query) return feedbacks;
    return feedbacks.filter((fb) => getUserName(fb).toLowerCase().includes(query));
  }, [feedbacks, searchInput]);

  const averageRating = useMemo(() => {
    const ratings = feedbacks
      .map((fb) => Number(fb.rating))
      .filter((value) => Number.isFinite(value) && value > 0);
    if (!ratings.length) return null;
    return Math.round((ratings.reduce((sum, value) => sum + value, 0) / ratings.length) * 10) / 10;
  }, [feedbacks]);

  const handleResetFilters = () => {
    setSearchInput("");
    setPage(0);
    setFilterKey((key) => key + 1);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/v1/feedback/${deleteTarget.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Feedback deleted");
      setDeleteTarget(null);
      fetchFeedbacks();
    } catch (err) {
      toast.error("Failed to delete feedback");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminPageLayout
      eyebrow="User Insights"
      title="Feedback Management"
      description="Review user feedback and ratings, search by user name, and manage feedback records."
      actions={
        <div
          className="admin-toolbar"
          style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: "12px",
              border: "1px solid rgba(63,122,42,0.18)",
              background: "rgba(63,122,42,0.06)",
              minHeight: 40,
            }}
          >
            <Typography variant="body2" sx={{ color: "#57534e", whiteSpace: "nowrap" }}>
              Average Rating
            </Typography>
            {loading ? (
              <CircularProgress size={16} sx={{ color: "#3f7a2a", mx: 0.5 }} />
            ) : averageRating == null ? (
              <Typography variant="body2" fontWeight={700} sx={{ color: "#78716c", whiteSpace: "nowrap" }}>
                —
              </Typography>
            ) : (
              <>
                <Rating value={averageRating} precision={0.1} readOnly size="small" />
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ color: "#172112", whiteSpace: "nowrap" }}
                >
                  {averageRating.toFixed(1)}/5
                </Typography>
              </>
            )}
          </Box>
          <TextField
            key={`search-${filterKey}`}
            id="feedback-search"
            label="Search by user name"
            value={searchInput}
            size="small"
            sx={{ minWidth: 220 }}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(0);
            }}
          />
          <AdminAction
            id="reset-filters-btn"
            variant="outline"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={handleResetFilters}
            sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
          >
            Reset
          </AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Feedback list"
        description="Browse feedback messages and ratings submitted by app users."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 700 }} aria-label="feedback table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>User</StyledTableCell>
                <StyledTableCell>Rating</StyledTableCell>
                <StyledTableCell>Message</StyledTableCell>
                <StyledTableCell>Date</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        py: 4,
                        color: "#78716c",
                      }}
                    >
                      <CircularProgress size={22} sx={{ color: "#3f7a2a" }} />
                      <span>Loading feedback…</span>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ) : filteredRows.length !== 0 ? (
                filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((fb) => {
                    const preview = getMessagePreview(fb.message);
                    return (
                      <StyledTableRow key={fb.id}>
                        <StyledTableCell>
                          <div className="font-semibold text-[15px] text-[#172112]">
                            {getUserName(fb) || fb.user?.mobile || "N/A"}
                          </div>
                        </StyledTableCell>
                        <StyledTableCell>
                          {fb.rating ? <Rating value={fb.rating} readOnly size="small" /> : "—"}
                        </StyledTableCell>
                        <StyledTableCell>
                          {preview ? (
                            <button
                              type="button"
                              onClick={() => setViewTarget(fb)}
                              title={preview}
                              className="text-left text-[#172112] hover:text-[#3f7a2a] hover:underline"
                              style={{
                                maxWidth: 320,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                font: "inherit",
                              }}
                            >
                              {preview}
                            </button>
                          ) : (
                            <span className="text-zinc-400 italic">No message</span>
                          )}
                        </StyledTableCell>
                        <StyledTableCell>
                          {new Date(fb.createdAt).toLocaleDateString()}
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <TableRowActions
                            rowId={fb.id}
                            onView={() => setViewTarget(fb)}
                            onDelete={() => setDeleteTarget(fb)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} align="center">
                    <Box sx={{ py: 4, color: "#78716c" }}>No feedback found.</Box>
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={loading ? 0 : filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </AdminSectionCard>

      <ViewFeedbackDialog
        open={!!viewTarget}
        feedback={viewTarget}
        onClose={() => setViewTarget(null)}
      />

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Feedback"
        itemName={`Feedback #${deleteTarget?.id}`}
        loading={deleting}
      />
    </AdminPageLayout>
  );
}
